import {
  type GraphPath,
  IPF_NETWORK_VIEWER,
  IPF_NETWORK_VIEWER_LABEL,
  removeAccents,
  safeAbsurd,
} from '@ipfabric/utils';
import { format } from 'date-fns-tz';
import {
  type GraphState,
  isPathLookupGraphMetadata,
  isTopologyGraphMetadata,
} from '../../GraphState';

const getPathLabel = (id: GraphPath): string =>
  id === IPF_NETWORK_VIEWER ? IPF_NETWORK_VIEWER_LABEL : id;

const createGraphExportRawBaseFileName = (state: GraphState): string => {
  const loadedViewName = state.loadViewDialog.loadedView?.name;
  if (loadedViewName) {
    return loadedViewName;
  }

  const metadata = state.graphMetadata;
  if (isTopologyGraphMetadata(metadata)) {
    if (metadata.parameters.paths.length === 1) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      return getPathLabel(metadata.parameters.paths[0]!);
    }
    if (metadata.parameters.paths.length > 1) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      return `${getPathLabel(metadata.parameters.paths[0]!)}-${metadata.parameters.paths.length}_total`;
    }
  } else if (isPathLookupGraphMetadata(metadata)) {
    switch (metadata.parameters.pathLookupType) {
      case 'unicast':
        return `unicast-${metadata.parameters.startingPoint}-${metadata.parameters.destinationPoint}`;
      case 'multicast':
        return `multicast-${metadata.parameters.source}-${metadata.parameters.group}`;
      case 'hostToDefaultGW':
        return `defaultGateway-${metadata.parameters.startingPoint}`;
      default:
        safeAbsurd(metadata.parameters);
    }
  }

  return '';
};

const createGraphExportBaseFileName = (state: GraphState): string => {
  const fileName = createGraphExportRawBaseFileName(state);
  const sanitized = removeAccents(fileName).replace(/[^a-zA-Z0-9-.]+/g, '_');
  if (sanitized.match(/^_*$/)) {
    // no characters left after sanitization - discard
    return 'graph';
  }

  return sanitized;
};

export const createGraphExportFileName = (state: GraphState): string => {
  const now = new Date();
  return `${createGraphExportBaseFileName(state)}-${format(
    now.getTime() + now.getTimezoneOffset() * 60000,
    'yyyy-MM-dd_HH-mm-ss',
  )}Z`; // Z is for Zulu time (UTC)
};
